import type { Claim, DriftIssue, ProjectModel } from "../../types.js";
import { scanProjectModel } from "../../scanner/manifest.js";

/**
 * Known infrastructure, platforms, services, and concepts that appear in scaffold docs
 * but are NOT installable packages. These are excluded from DEPENDENCY_MISSING checks.
 */
const KNOWN_NON_PACKAGES = new Set([
  // Runtimes & languages
  "node.js", "node", "nodejs", "python", "cpython", "go", "golang",
  "rust", "ruby", "java", "jdk", "jre", "deno", "bun", "swift", "kotlin",
  "typescript", "javascript", ".net", "c#", "php", "elixir", "scala", "dart",
  // Package managers & build tools
  "npm", "pnpm", "yarn", "bun", "pip", "poetry", "cargo", "maven", "gradle",
  "cocoapods", "carthage", "spm", "homebrew", "apt", "nix", "turbo", "turborepo",
  "nx", "lerna", "vite", "webpack", "esbuild", "rollup", "tsup", "swc", "babel",
  // Databases & data stores
  "sqlite", "sqlite3", "postgresql", "postgres", "mysql", "mariadb",
  "mongodb", "mongo", "redis", "elasticsearch", "opensearch",
  "dynamodb", "cassandra", "cockroachdb", "supabase", "firebase",
  "firestore", "neon", "planetscale", "turso",
  // Infrastructure & cloud
  "docker", "kubernetes", "k8s", "helm", "terraform", "pulumi",
  "aws", "gcp", "azure", "vercel", "netlify", "fly.io", "railway",
  "heroku", "cloudflare", "digitalocean", "hetzner", "linode",
  "s3", "ec2", "lambda", "cloud run", "cloud functions", "ecs", "fargate",
  // Auth & identity services
  "keycloak", "auth0", "okta", "firebase auth", "clerk",
  "azure entra id", "azure ad", "active directory", "cognito",
  "oauth", "oauth 2.0", "saml", "oidc", "openid connect", "jwt",
  // Monitoring & observability
  "langfuse", "grafana", "prometheus", "datadog", "sentry", "newrelic",
  "new relic", "honeycomb", "jaeger", "zipkin", "opentelemetry",
  "logstash", "kibana", "splunk", "pagerduty",
  // Message queues & event systems
  "rabbitmq", "kafka", "nats", "pulsar", "sqs", "sns", "eventbridge",
  "redis pub/sub", "bullmq",
  // CI/CD & dev tools
  "github actions", "gitlab ci", "jenkins", "circleci", "bitbucket pipelines",
  "argocd", "flux",
  // Protocols & patterns
  "rest", "rest api", "graphql", "grpc", "websocket", "websockets",
  "mqtt", "amqp", "http/2", "http/3", "sse", "server-sent events",
  // Architecture concepts (not packages)
  "frontend", "backend", "database", "database layer", "service layer",
  "microservices", "monolith", "serverless", "edge functions",
  "cdn", "reverse proxy", "load balancer", "api gateway",
  // CSS frameworks (often referenced by name, not always in package.json)
  "tailwind css", "tailwind", "bootstrap",
  // AI / LLM services & platforms
  "openai api", "openai platform", "azure openai", "anthropic", "google ai",
  "hugging face", "huggingface", "cohere", "mistral", "ollama",
  "llm", "llms", "large language model",
  // Storage & CDN services
  "cloudinary", "imgix", "bunny.net", "fastly",
  "google cloud storage", "azure blob storage", "r2",
  // Payment & comms services
  "stripe", "paddle", "lemon squeezy", "twilio", "sendgrid", "resend", "postmark",
  // Additional common architectural terms
  "api", "rest endpoint", "http", "https", "webhook", "webhooks",
  "cron", "cron job", "background job", "background jobs", "worker",
  "rate limiting", "rate limit", "caching", "cache",
  "pagination", "search", "full-text search",
  "feature flag", "feature flags", "a/b testing",
  "error tracking", "logging", "log aggregation",
  "event sourcing", "cqrs", "ddd",
]);

/** Patterns that indicate infrastructure/services rather than packages */
const NON_PACKAGE_PATTERNS = [
  /\s+\/\s+/,        // "Keycloak / Azure Entra ID" — space-slash-space = multi-tool mention (not @scope/pkg)
  /\s(api|sdk|cli|ui|platform|service|server|cloud|saas|layer)$/i,
  /^(self[- ]hosted|managed|hosted)/i,
];

/**
 * Common aliases: how people refer to packages in docs vs what's in package.json.
 * Maps "human name" (lowercase) → package name(s) to search for.
 */
const KNOWN_ALIASES = new Map<string, string[]>([
  // Frameworks
  ["next.js", ["next"]],
  ["next", ["next"]],
  ["nuxt.js", ["nuxt"]],
  ["nuxt", ["nuxt"]],
  ["vue.js", ["vue"]],
  ["vue", ["vue"]],
  ["react", ["react"]],
  ["angular", ["@angular/core"]],
  ["svelte", ["svelte"]],
  ["solid.js", ["solid-js"]],
  ["solid", ["solid-js"]],
  ["remix", ["@remix-run/react", "@remix-run/node"]],
  ["astro", ["astro"]],
  ["express", ["express"]],
  ["fastify", ["fastify"]],
  ["hono", ["hono"]],
  ["nest.js", ["@nestjs/core"]],
  ["nestjs", ["@nestjs/core"]],
  ["koa", ["koa"]],
  // UI & styling
  ["tailwind", ["tailwindcss"]],
  ["tailwind css", ["tailwindcss"]],
  ["shadcn", ["class-variance-authority", "@radix-ui"]],
  ["shadcn/ui", ["class-variance-authority", "@radix-ui"]],
  ["radix", ["@radix-ui"]],
  ["radix ui", ["@radix-ui"]],
  ["material ui", ["@mui/material"]],
  ["mui", ["@mui/material"]],
  ["ant design", ["antd"]],
  ["chakra", ["@chakra-ui/react"]],
  ["chakra ui", ["@chakra-ui/react"]],
  ["styled components", ["styled-components"]],
  ["styled-components", ["styled-components"]],
  ["framer motion", ["framer-motion"]],
  ["lucide", ["lucide-react"]],
  ["heroicons", ["@heroicons/react"]],
  // State management
  ["react query", ["@tanstack/react-query"]],
  ["tanstack query", ["@tanstack/react-query"]],
  ["tanstack router", ["@tanstack/react-router"]],
  ["redux", ["@reduxjs/toolkit", "redux"]],
  ["zustand", ["zustand"]],
  ["jotai", ["jotai"]],
  ["recoil", ["recoil"]],
  // Data & forms
  ["prisma", ["prisma", "@prisma/client"]],
  ["drizzle", ["drizzle-orm"]],
  ["typeorm", ["typeorm"]],
  ["sequelize", ["sequelize"]],
  ["mongoose", ["mongoose"]],
  ["react hook form", ["react-hook-form"]],
  ["formik", ["formik"]],
  // Auth
  ["next-auth", ["next-auth", "@auth/core"]],
  ["nextauth", ["next-auth", "@auth/core"]],
  ["passport", ["passport"]],
  ["lucia", ["lucia"]],
  // Validation
  ["zod", ["zod"]],
  ["yup", ["yup"]],
  ["joi", ["joi"]],
  // API & communication
  ["trpc", ["@trpc/server", "@trpc/client"]],
  ["axios", ["axios"]],
  ["socket.io", ["socket.io"]],
  // Testing
  ["jest", ["jest"]],
  ["vitest", ["vitest"]],
  ["playwright", ["@playwright/test", "playwright"]],
  ["cypress", ["cypress"]],
  ["testing library", ["@testing-library/react"]],
  // i18n
  ["next-intl", ["next-intl"]],
  ["i18next", ["i18next"]],
  ["react-intl", ["react-intl"]],
  // Misc
  ["openai", ["openai"]],
  ["langchain", ["langchain", "@langchain/core"]],
  ["cva", ["class-variance-authority"]],
  ["class-variance-authority", ["class-variance-authority"]],
  ["jose", ["jose"]],
  ["jsonwebtoken", ["jsonwebtoken"]],
  ["date-fns", ["date-fns"]],
  ["dayjs", ["dayjs"]],
  ["lodash", ["lodash"]],
  ["underscore", ["underscore"]],
]);

/** Check that claimed dependencies exist in manifests */
export function checkDependencies(
  claims: Claim[],
  projectRoot: string,
  project?: ProjectModel
): DriftIssue[] {
  const issues: DriftIssue[] = [];
  const deps = loadAllDependencies(projectRoot, project);
  if (!deps) return issues;

  const depClaims = claims.filter(
    (c) => c.kind === "dependency" && !c.negated
  );
  const versionClaims = claims.filter(
    (c) => c.kind === "version" && !c.negated
  );

  for (const claim of depClaims) {
    const name = claim.value.toLowerCase();

    // Skip known infrastructure/services — they won't be in package.json
    if (KNOWN_NON_PACKAGES.has(name)) continue;

    // Skip if any word in a multi-word claim matches a known non-package
    // e.g. "Keycloak / Azure Entra ID" → split → check each part
    const parts = claim.value.split(/\s*[\/,&]\s*/).map((p) => p.trim().toLowerCase());
    if (parts.length > 1 && parts.every((p) => KNOWN_NON_PACKAGES.has(p))) continue;

    // Skip pattern matches (things that look like services, not packages)
    if (NON_PACKAGE_PATTERNS.some((re) => re.test(claim.value))) continue;

    // Skip claims with low confidence — they're likely descriptive, not literal
    if (claim.confidence === "low") continue;

    // Multi-layer matching:
    // 1. Known alias map (covers "Next.js"→"next", "shadcn"→"class-variance-authority", etc.)
    // 2. Exact name match (lowercase)
    // 3. Normalized match (.js/.ts suffix stripped)
    // 4. Scoped package prefix ("radix" matches "@radix-ui/*")
    const aliases = KNOWN_ALIASES.get(name);
    const normalized = name.replace(/\.js$/, "").replace(/\.ts$/, "").replace(/[^a-z0-9@/_-]/g, "");

    const found = deps.find((d) => {
      const depName = d.name.toLowerCase();
      // Exact or normalized match
      if (depName === name || depName === normalized || depName.endsWith(`/${normalized}`)) return true;
      // Alias match: any of the known package names for this alias
      if (aliases?.some((alias) => depName === alias || depName.startsWith(`${alias}/`))) return true;
      // Scoped prefix match: "radix" matches "@radix-ui/react-dialog"
      if (depName.includes(`/${normalized}`) || depName.startsWith(`@${normalized}/`) || depName.startsWith(`@${normalized}-`)) return true;
      return false;
    });
    if (!found) {
      issues.push({
        code: "DEPENDENCY_MISSING",
        severity: "warning",
        file: claim.source,
        line: claim.line,
        message: `Claimed dependency "${claim.value}" not found in any manifest`,
        claim,
        suggestion: `Either remove "${claim.value}" from the scaffold, or add the actual package name to the project manifest if it is truly required.`,
      });
    }
  }

  for (const claim of versionClaims) {
    // Parse "React 18" or "Node v20"
    const match = claim.value.match(/^(.+?)\s+v?(\d[\d.]*\S*)$/);
    if (!match) continue;

    const name = match[1].trim().toLowerCase();
    const claimedVersion = match[2];
    const found = deps.find(
      (d) => d.name.toLowerCase() === name
    );

    if (found && !found.version.includes(claimedVersion)) {
      issues.push({
        code: "VERSION_MISMATCH",
        severity: "warning",
        file: claim.source,
        line: claim.line,
        message: `Claimed "${claim.value}" but manifest has version "${found.version}"`,
        claim,
        suggestion: `Update the scaffold version reference for "${claim.value}" to match manifest version "${found.version}", or update the manifest if the scaffold is the source of truth.`,
      });
    }
  }

  return issues;
}

interface DepEntry {
  name: string;
  version: string;
}

function loadAllDependencies(projectRoot: string, project?: ProjectModel): DepEntry[] | null {
  // Use the pre-scanned project model if provided — avoids a redundant scanProjectModel call
  const model: ProjectModel = project ?? scanProjectModel(projectRoot);
  const entries: DepEntry[] = [];

  // Collect from root manifest
  const manifests = model.rootManifest ? [model.rootManifest] : [];
  // Also collect from all workspace manifests (monorepo support)
  for (const ws of model.workspaces) {
    manifests.push(ws.manifest);
  }

  for (const manifest of manifests) {
    for (const [name, version] of Object.entries(manifest.dependencies ?? {})) {
      entries.push({ name, version: String(version) });
      addAliasEntries(entries, name, version);
    }
    for (const [name, version] of Object.entries(manifest.devDependencies ?? {})) {
      entries.push({ name, version: String(version) });
      addAliasEntries(entries, name, version);
    }
  }

  return entries.length ? entries : null;
}

function addAliasEntries(
  entries: DepEntry[],
  name: string,
  version: unknown
): void {
  if (!name.includes("/")) return;
  const alias = name.split("/").pop();
  if (!alias || alias === name) return;
  entries.push({ name: alias, version: String(version) });
}
